
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {MarginRateProfileStatus} from './MarginRateProfileStatus';
import {FieldModelMarginRateProfileStatus} from './MarginRateProfileStatus';
import {MarginRateProfilePositionMode} from './MarginRateProfilePositionMode';
import {FieldModelMarginRateProfilePositionMode} from './MarginRateProfilePositionMode';

/**
 * MarginRateProfile struct
 */
class MarginRateProfile {
  /**
   * Initialize struct
   * @param {!UUID=} id
   * @param {!string=} name
   * @param {!string=} comment
   * @param {!string=} description
   * @param {!Date=} activationTime
   * @param {!MarginRateProfileStatus=} status
   * @param {!MarginRateProfilePositionMode=} positionMode
   * @param {!Map=} percentage
   * @param {!Set=} accountGroupId
   * @param {!number=} sortOrder
   * @constructor
   */
  constructor (id = UUID.sequential(), name = '', comment = '', description = '', activationTime = new Date(0), status = new MarginRateProfileStatus(), positionMode = new MarginRateProfilePositionMode(), percentage = new Map(), accountGroupId = new Set(), sortOrder = 0) {
    this.id = id
    this.Name = name
    this.Comment = comment
    this.Description = description
    this.ActivationTime = activationTime
    this.Status = status
    this.PositionMode = positionMode
    this.Percentage = percentage
    this.AccountGroupId = accountGroupId
    this.SortOrder = sortOrder
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MarginRateProfile}
   * @param {!MarginRateProfile} other Other struct
   * @returns {!MarginRateProfile} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = new UUID(other.id)
    } else {
      this.id = null
    }
    if (other.Name != null) {
      this.Name = other.Name
    } else {
      this.Name = null
    }
    if (other.Comment != null) {
      this.Comment = other.Comment
    } else {
      this.Comment = null
    }
    if (other.Description != null) {
      this.Description = other.Description
    } else {
      this.Description = null
    }
    if (other.ActivationTime != null) {
      if (other.ActivationTime instanceof Date) {
        this.ActivationTime = new Date(other.ActivationTime.getTime())
      } else {
        this.ActivationTime = new Date(Math.round(other.ActivationTime / 1000000))
      }
    } else {
      this.ActivationTime = null
    }
    if (other.Status != null) {
      this.Status = MarginRateProfileStatus.fromObject(other.Status)
    } else {
      this.Status = null
    }
    if (other.PositionMode != null) {
      this.PositionMode = MarginRateProfilePositionMode.fromObject(other.PositionMode)
    } else {
      this.PositionMode = null
    }
    if (other.Percentage != null) {
      this.Percentage = new Map()
      Object.keys(other.Percentage).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = new UUID(key)
          if (other.Percentage[key] != null) {
            let tempValue
            tempValue = other.Percentage[key]
            this.Percentage.set(tempKey, tempValue)
          } else {
            this.Percentage.set(tempKey, null)
          }
        }
      })
    } else {
      this.Percentage = null
    }
    if (other.AccountGroupId != null) {
      this.AccountGroupId = new Set()
      for (let item of other.AccountGroupId) {
        if (item != null) {
          let tempItem
          tempItem = new UUID(item)
          this.AccountGroupId.add(tempItem)
        } else {
          this.AccountGroupId.add(null)
        }
      }
    } else {
      this.AccountGroupId = null
    }
    if (other.SortOrder != null) {
      this.SortOrder = other.SortOrder
    } else {
      this.SortOrder = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MarginRateProfile}
   * @returns {!MarginRateProfile} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MarginRateProfileModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MarginRateProfileModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MarginRateProfile}
   * @param {!MarginRateProfile} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MarginRateProfile)) {
      throw new TypeError('Instance of MarginRateProfile is required!')
    }
    // noinspection RedundantIfStatementJS
    if (this.id != null) {
      if ((other.id == null) || !this.id.eq(other.id)) {
        return false
      }
    } else if (other.id != null) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MarginRateProfile}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id.toString() : null),
      Name: ((this.Name != null) ? this.Name : null),
      Comment: ((this.Comment != null) ? this.Comment : null),
      Description: ((this.Description != null) ? this.Description : null),
      ActivationTime: ((this.ActivationTime != null) ? (this.ActivationTime.getTime() * 1000000) : null),
      Status: ((this.Status != null) ? this.Status : null),
      PositionMode: ((this.PositionMode != null) ? this.PositionMode : null),
      Percentage: ((this.Percentage != null) ? fbe.MapToObject(new Map(Array.from(this.Percentage, item => [((item[0] != null) ? item[0].toString() : null), ((item[1] != null) ? item[1] : null)]))) : null),
      AccountGroupId: ((this.AccountGroupId != null) ? Array.from(this.AccountGroupId, item => ((item != null) ? item.toString() : null)) : null),
      SortOrder: ((this.SortOrder != null) ? this.SortOrder : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MarginRateProfile.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MarginRateProfile} other Object value
   * @returns {!MarginRateProfile} Created struct
   */
  static fromObject (other) {
    return new MarginRateProfile().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MarginRateProfile}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MarginRateProfile.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MarginRateProfile}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 92
  }
}

export { MarginRateProfile };

/**
 * Fast Binary Encoding MarginRateProfile field model
 */
class FieldModelMarginRateProfile extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.Name = new fbe.FieldModelString(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.Comment = new fbe.FieldModelString(buffer, this.Name.fbeOffset + this.Name.fbeSize)
    this.Description = new fbe.FieldModelString(buffer, this.Comment.fbeOffset + this.Comment.fbeSize)
    this.ActivationTime = new fbe.FieldModelTimestamp(buffer, this.Description.fbeOffset + this.Description.fbeSize)
    this.Status = new FieldModelMarginRateProfileStatus(buffer, this.ActivationTime.fbeOffset + this.ActivationTime.fbeSize)
    this.PositionMode = new FieldModelMarginRateProfilePositionMode(buffer, this.Status.fbeOffset + this.Status.fbeSize)
    this.Percentage = new fbe.FieldModelMap(new fbe.FieldModelUUID(buffer, this.PositionMode.fbeOffset + this.PositionMode.fbeSize), new fbe.FieldModelDouble(buffer, this.PositionMode.fbeOffset + this.PositionMode.fbeSize), buffer, this.PositionMode.fbeOffset + this.PositionMode.fbeSize)
    this.AccountGroupId = new fbe.FieldModelSet(new fbe.FieldModelUUID(buffer, this.Percentage.fbeOffset + this.Percentage.fbeSize), buffer, this.Percentage.fbeOffset + this.Percentage.fbeSize)
    this.SortOrder = new fbe.FieldModelUInt32(buffer, this.AccountGroupId.fbeOffset + this.AccountGroupId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelMarginRateProfile}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMarginRateProfile}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.Name.fbeSize + this.Comment.fbeSize + this.Description.fbeSize + this.ActivationTime.fbeSize + this.Status.fbeSize + this.PositionMode.fbeSize + this.Percentage.fbeSize + this.AccountGroupId.fbeSize + this.SortOrder.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMarginRateProfile}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.Name.fbeExtra + this.Comment.fbeExtra + this.Description.fbeExtra + this.ActivationTime.fbeExtra + this.Status.fbeExtra + this.PositionMode.fbeExtra + this.Percentage.fbeExtra + this.AccountGroupId.fbeExtra + this.SortOrder.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMarginRateProfile}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMarginRateProfile.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMarginRateProfile}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 92
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMarginRateProfile}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMarginRateProfile}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Comment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.ActivationTime.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ActivationTime.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ActivationTime.fbeSize

    if ((fbeCurrentSize + this.Status.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Status.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Status.fbeSize

    if ((fbeCurrentSize + this.PositionMode.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PositionMode.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PositionMode.fbeSize

    if ((fbeCurrentSize + this.Percentage.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Percentage.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Percentage.fbeSize

    if ((fbeCurrentSize + this.AccountGroupId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountGroupId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountGroupId.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SortOrder.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMarginRateProfile}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMarginRateProfile}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMarginRateProfile}
   * @param {!MarginRateProfile} fbeValue Default value, defaults is new MarginRateProfile()
   * @returns {!MarginRateProfile} MarginRateProfile value
   */
  get (fbeValue = new MarginRateProfile()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMarginRateProfile}
   * @param {!MarginRateProfile} fbeValue MarginRateProfile value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get(UUID.sequential())
    } else {
      fbeValue.id = UUID.sequential()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) <= fbeStructSize) {
      fbeValue.Comment = this.Comment.get()
    } else {
      fbeValue.Comment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.ActivationTime.fbeSize) <= fbeStructSize) {
      fbeValue.ActivationTime = this.ActivationTime.get()
    } else {
      fbeValue.ActivationTime = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ActivationTime.fbeSize

    if ((fbeCurrentSize + this.Status.fbeSize) <= fbeStructSize) {
      fbeValue.Status = this.Status.get()
    } else {
      fbeValue.Status = new MarginRateProfileStatus()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Status.fbeSize

    if ((fbeCurrentSize + this.PositionMode.fbeSize) <= fbeStructSize) {
      fbeValue.PositionMode = this.PositionMode.get()
    } else {
      fbeValue.PositionMode = new MarginRateProfilePositionMode()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PositionMode.fbeSize

    if ((fbeCurrentSize + this.Percentage.fbeSize) <= fbeStructSize) {
      this.Percentage.get(fbeValue.Percentage)
    } else {
      fbeValue.Percentage.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Percentage.fbeSize

    if ((fbeCurrentSize + this.AccountGroupId.fbeSize) <= fbeStructSize) {
      this.AccountGroupId.get(fbeValue.AccountGroupId)
    } else {
      fbeValue.AccountGroupId.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountGroupId.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) <= fbeStructSize) {
      fbeValue.SortOrder = this.SortOrder.get()
    } else {
      fbeValue.SortOrder = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMarginRateProfile}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMarginRateProfile}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMarginRateProfile}
   * @param {!MarginRateProfile} fbeValue MarginRateProfile value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMarginRateProfile}
   * @param {MarginRateProfile} fbeValue MarginRateProfile value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.Name.set(fbeValue.Name)
    this.Comment.set(fbeValue.Comment)
    this.Description.set(fbeValue.Description)
    this.ActivationTime.set(fbeValue.ActivationTime)
    this.Status.set(fbeValue.Status)
    this.PositionMode.set(fbeValue.PositionMode)
    this.Percentage.set(fbeValue.Percentage)
    this.AccountGroupId.set(fbeValue.AccountGroupId)
    this.SortOrder.set(fbeValue.SortOrder)
  }
}

export { FieldModelMarginRateProfile };

/**
 * Fast Binary Encoding MarginRateProfile model
 */
class MarginRateProfileModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMarginRateProfile(this.buffer, 4)
  }

  /**
   * Get the MarginRateProfile model
   * @this {!MarginRateProfileModel}
   * @returns {!FieldModelMarginRateProfile} model MarginRateProfile model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MarginRateProfileModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MarginRateProfileModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MarginRateProfileModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MarginRateProfileModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMarginRateProfile.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MarginRateProfileModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MarginRateProfileModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MarginRateProfileModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MarginRateProfileModel}
   * @param {!MarginRateProfile} value MarginRateProfile value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MarginRateProfileModel}
   * @param {!MarginRateProfile} value MarginRateProfile value, defaults is new MarginRateProfile()
   * @return {!object} Deserialized MarginRateProfile value and its size
   */
  deserialize (value = new MarginRateProfile()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MarginRateProfile(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MarginRateProfile(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MarginRateProfileModel}
   * @param {!number} prev Previous MarginRateProfile model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MarginRateProfileModel };
